// Copyright 2024-2026 - Hewlett Packard Enterprise Company
import { useContext, useEffect } from 'react';
import { getLocale } from '../../utils/i18nHelpers';
import { AuthContext } from '../../contexts/AuthContext';
// import { isDevEnvAuth } from '../../utils/authHelpers';

const GREENLAKE_URL =
  window.indigo.REACT_APP_GREENLAKE_URL || 'https://common.cloud.hpe.com';

/**
 * https://developer.greenlake.hpe.com/docs/greenlake/guides/internal/front-end/greenlake_header/
 */
export default function GreenlakeHeader() {
  const { token, isAuthenticated, getUserName, getUserEmail } =
    useContext(AuthContext);
  const isAuth = isAuthenticated();

  useEffect(() => {
    let pushStateListener;
    let isMounted = true;
    const header = document.querySelector('greenlake-header');

    if (!header) return;
    if (!isAuth) {
      header.platform = 'publicDefault';
      return;
    }

    const configureHeader = async () => {
      if (!isMounted) return;
      const i18nLocale = getLocale();

      header.linkbaseurl = GREENLAKE_URL;
      header.logouturl = `${window?.location?.origin}/logout`;
      header.language = i18nLocale;

      header.config = {
        ...(header?.config || {}),
      };

      /*
      // workarounds for GLCP's header trying to read info from a bogus JWT in dev envs
      if (isDevEnvAuth()) {
        const { givenName, lastName } = await getUserName();
        const userEmail = await getUserEmail();
        // set and re-assert the user info as the route changes to override the GLP header reading
        // the user details from the oidc session storage
        pushStateListener = () => {
          header.config.user = {
            firstName: givenName,
            lastName,
            email: userEmail,
          };
        };
        pushStateListener();
        window.addEventListener('pushState', pushStateListener);
      }
      */

      header.platform = 'secureDefault';
      /*
       * the known valid config items include:
       * 'brand' - the HPE logo on left side of navbar
       * 'nav' - GLCP's center navigation
       * 'bell' - notification center bell icon
       * 'help' - the ? icon for help and feedback
       * 'apps' - the rubrik's cube app menu
       * 'context' - context sensitive help (requires faking out LD flags)
       * 'workspace' - workspace menu (requires LD flags and also setting the workspace name)
       * 'user - user profile menu
       */
      header.config.items = ['brand', 'help', 'apps', 'context', 'user'];
      header.ldFlags = {
        ...header.ldFlags,
        // for contextual help
        'glcp-contextual-help': true,
        // for the workspace menu
        // 'glcp-service-centric-experience-phase-1': true,
      };

      /*
      const helpMenu = header?.shadowRoot?.querySelector('help-menu');
      if (!helpMenu) return;
      helpMenu.platform = 'secureDefault';
      */
    };

    configureHeader();

    // eslint-disable-next-line consistent-return
    return () => {
      isMounted = false;
      if (pushStateListener) {
        window.removeEventListener('pushState', pushStateListener);
      }
    };
  }, [token, isAuth, getUserName, getUserEmail]);

  // nothing visually to return, the header's container is in the index.html
  return null;
}
